import { useEffect, useRef, useState } from 'react';

export interface FadeInStyle {
  opacity: number;
  transform: string;
  transition: string;
}

/**
 * Returns a ref and a style object that fade-and-slide an element in
 * when it enters the viewport (via IntersectionObserver).
 *
 * @param delay   extra delay in ms (use to stagger siblings)
 * @param distance  translate-Y distance in px
 */
export function useFadeIn<T extends HTMLElement = HTMLDivElement>(
  delay = 0,
  distance = 12,
): { ref: React.RefObject<T | null>; style: FadeInStyle } {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.05 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return {
    ref,
    style: {
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : `translateY(${distance}px)`,
      transition: `opacity 400ms ease ${delay}ms, transform 400ms ease ${delay}ms`,
    },
  };
}
